import { useEffect, useState, useMemo, useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppContext } from '../context/AppContext';
import { formatNumber } from '../utils/formatNumber';

const tabOptions = [
  { key: 'my', label: 'my_recipes'},
  { key: 'base', label: 'base_recipes' },
  { key: 'others', label: 'other_users_recipes' }
];

const RecipeList = ({ onEdit, api, onClone }) => {
  const { t } = useTranslation();
  const { user, categories, language, token } = useAppContext();
  const visibleTabs = useMemo(() => user ? tabOptions : tabOptions.slice(1), [user]);
  const [tab, setTab] = useState(0);
  const [recipes, setRecipes] = useState([]);
  const [page, setPage] = useState([0, 0, 0]);
  const [rowsPerPage, setRowsPerPage] = useState([10, 10, 10]);
  const [total, setTotal] = useState([0, 0, 0]);
  const [search, setSearch] = useState('');
  const [firstAutoTab, setFirstAutoTab] = useState(true);
  const [loading, setLoading] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  // Memoize category names for fast lookup
  const categoriesNames = useMemo(() => {
    const acc = {};
    for (const cat of categories) {
      acc[cat.id] = cat.translations?.[language]?.name || cat.name;
    }
    return acc;
  }, [categories, language]);

  // Fetch recipes
  const fetchRecipes = useCallback(async (tabIdx = tab, pageArg, perPageArg, searchArg) => {
    if (user && !token) return; // Wait for token before fetching private lists
    const list = visibleTabs[tabIdx].key;
    const pageVal = typeof pageArg === 'number' ? pageArg : page[tabIdx];
    const perPageVal = typeof perPageArg === 'number' ? perPageArg : rowsPerPage[tabIdx];
    const searchVal = typeof searchArg === 'string' ? searchArg : search;
    try {
      setLoading(true);
      const data = await api.getRecipes({ list, page: pageVal + 1, per_page: perPageVal, search: searchVal, lang: language });
      setLoading(false);
      if (data && Array.isArray(data.data)) {
        setRecipes(data.data);
        setTotal(totals => {
          const newTotals = [...totals];
          newTotals[tabIdx] = data.total || 0;
          return newTotals;
        });
        if (firstAutoTab) {
          setFirstAutoTab(false);
          if (user && tab === 0 && !search && data.data.length === 0) {
            setTab(1);
          }
        }
      }
    } catch (error) {
      setLoading(false);
      console.error('Error fetching recipes:', error);
    }
    // eslint-disable-next-line
  }, [api, visibleTabs, tab, page, rowsPerPage, search, user, firstAutoTab, language, token]);

  // Reset tab if user changes (login/logout)
  useEffect(() => {
    setTab(0);
  }, [user]);

  // Fetch recipes when dependencies change
  useEffect(() => {
    if (user && !token) return;
    fetchRecipes(tab);
    // eslint-disable-next-line
  }, [api, tab, page[tab], rowsPerPage[tab], search, user, language, token]);

  // Handlers
  const handleDelete = useCallback(async (id) => {
    if (!window.confirm(t('confirm_delete_recipe'))) return;
    await api.deleteRecipe(id);
    fetchRecipes(tab);
    setMenuAnchor(null);
    setSelectedRecipe(null);
  }, [api, fetchRecipes, tab, t]);

  const handleChangePage = (event, newPage) => {
    setPage(p => {
      const arr = [...p];
      arr[tab] = newPage;
      return arr;
    });
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(rp => {
      const arr = [...rp];
      arr[tab] = value;
      return arr;
    });
    setPage(p => {
      const arr = [...p];
      arr[tab] = 0;
      return arr;
    });
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  // Avoid unnecessary renders for search
  const handleSearchChange = useCallback(e => {
    setSearch(e.target.value);
    setPage(p => {
      const arr = [...p];
      arr[tab] = 0;
      return arr;
    });
  }, [tab]);

  const handleMenuOpen = (event, recipe) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedRecipe(recipe);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRecipe(null);
  };

  const handleRowClick = (recipe) => {
    if (recipe.can_edit && onEdit) {
      onEdit(recipe);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const showStatusColumn = visibleTabs[tab]?.key === 'my';
  const columnCount = isMobile ? 2 : (showStatusColumn ? 5 : 4);

  const handleMenuEdit = () => {
    if (selectedRecipe && onEdit) {
      onEdit(selectedRecipe);
    }
    handleMenuClose();
  };

  const handleMenuDelete = () => {
    if (selectedRecipe) {
      handleDelete(selectedRecipe.id);
    }
  };

  const handleMenuClone = () => {
    if (selectedRecipe && onClone) {
      onClone(selectedRecipe);
    }
    handleMenuClose();
  };

  return (
    <div sx={{ width: '100%', overflow: 'hidden' }}>
      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }} variant="fullWidth">
        {visibleTabs.map((tabOpt) => (
          <Tab key={tabOpt.key} label={t(tabOpt.label)} />
        ))}
      </Tabs>
      <TextField
        label={t('search')}
        value={search}
        onChange={handleSearchChange}
        variant="outlined"
        size="small"
        sx={{ mb: 2, width: 320 }}
      />
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>{isMobile ? t('recipe') : t('name')}</TableCell>
              {!isMobile && <TableCell>{t('category')}</TableCell>}
              {!isMobile && <TableCell>{t('serving_temp')}</TableCell>}
              {!isMobile && showStatusColumn && <TableCell>{t('status')}</TableCell>}
              <TableCell align="right">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && recipes.map(recipe => (
              <TableRow 
                key={recipe.id} 
                hover 
                onClick={() => handleRowClick(recipe)}
                sx={{ cursor: recipe.can_edit ? 'pointer' : 'default' }}
              >
                <TableCell>
                  <Box component="span" sx={{ fontWeight: 'bold', display: 'block' }}>{recipe.name}</Box>
                  {isMobile && (
                    <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25, fontSize: '0.8rem', color: 'text.secondary' }}>
                      <span>{t('category')}: {categoriesNames[recipe.category_id] || '-'}</span>
                      <span>{t('serving_temp')}: {formatNumber(recipe.serving_temp, 0)}°C</span>
                      {showStatusColumn && <span>{t('status')}: {t(recipe.status) || recipe.status}</span>}
                    </Box>
                  )}
                </TableCell>
                {!isMobile && <TableCell>{categoriesNames[recipe.category_id] || '-'}</TableCell>}
                {!isMobile && <TableCell>{formatNumber(recipe.serving_temp, 0)}°C</TableCell>}
                {!isMobile && showStatusColumn && <TableCell>{t(recipe.status) || recipe.status}</TableCell>}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(event) => handleMenuOpen(event, recipe)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center">{t('loading') || 'Loading...'}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {selectedRecipe?.can_edit && (
          <MenuItem onClick={handleMenuEdit}>
            {t('edit')}
          </MenuItem>
        )}
        {selectedRecipe?.can_delete && (
          <MenuItem onClick={handleMenuDelete}>
            {t('delete')}
          </MenuItem>
        )}
        <MenuItem onClick={handleMenuClone}>
          {t('clone')}
        </MenuItem>
      </Menu>
      <TablePagination
        component="div"
        count={total[tab]}
        page={page[tab]}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage[tab]}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </div>
  );
};

export default RecipeList;